/**
 * Fire-and-forget POST to the functions-warmup Edge Function on app open.
 *
 * Triggers the Edge Function to warm the GATED tier immediately instead of
 * waiting up to ~4 min for the next pg_cron tick — worst-case warm latency
 * for a user collapses from minutes to seconds.
 *
 * Defended on the Edge Function side by:
 *  - Origin allowlist auth (no secret in the bundle).
 *  - Redis SETNX dedupe (30 s) — multiple tabs / opens within a window all
 *    fire the POST but only one actually warms.
 *
 * Deferred to requestIdleCallback so it never competes with hydration-
 * critical fetches (Privy session, /crash-state, etc.) for connection slots.
 *
 * SSR-safe: returns early on the server.
 */
export function useWarmupPing(): void {
  if (!import.meta.client) return

  const cfg = useRuntimeConfig()
  const url = `${cfg.public.apiBase}/functions-warmup`

  const fire = (): void => {
    $fetch(url, {
      method: 'POST',
      body: { source: 'app-open' },
    }).catch(() => {
      // Fire-and-forget — warmup failures must not surface to the app.
    })
  }

  // requestIdleCallback is the right primitive for "do after first paint
  // when the main thread is free". Safari < 16.4 / older browsers fall back
  // to setTimeout(0) which still defers to the next macrotask.
  if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
    ;(window as Window & {
      requestIdleCallback: (cb: () => void) => void
    }).requestIdleCallback(fire)
  } else {
    setTimeout(fire, 0)
  }
}
